import { Task, TaskStatus, TaskTypeId, WayPoint } from '@bringg/types';
import { first as _first } from 'lodash';

type StatusMapper = Partial<Record<TaskStatus, OrderTrackingStatus>>;

export enum OrderTrackingStatus {
	Confirmed,
	Received,
	DriverFound,
	FindingDriver,
	WayToPickup,
	PickingUp,
	OnPreviousDelivery,
	WayToDropoff,
	Arrived
}

export const getTrackingStatus = (
	task: Task,
	hasRoute: boolean,
	linkedTaskStatus: TaskStatus | null
): OrderTrackingStatus => {
	const statusMapper: StatusMapper = {
		[TaskStatus.Created]: OrderTrackingStatus.Confirmed,
		[TaskStatus.Unacknowledged]: OrderTrackingStatus.Received,
		[TaskStatus.Assigned]: OrderTrackingStatus.FindingDriver,
		[TaskStatus.Accepted]: getAcceptedStatus(task, hasRoute, linkedTaskStatus),
		[TaskStatus.Rejected]: OrderTrackingStatus.FindingDriver,
		[TaskStatus.OnTheWay]: getOnTheWayStatus(task),
		[TaskStatus.CheckedIn]: getCheckedInStatus(task),
		[TaskStatus.Done]: OrderTrackingStatus.Arrived
	};
	return statusMapper[task.status] ?? null;
};

export const getTrackingStatusByLinkedTaskStatus = (
	taskStatus: TaskStatus,
	taskType: TaskTypeId,
	linkedTaskStatus: TaskStatus
) => {
	if (taskStatus === TaskStatus.Accepted && taskType === TaskTypeId.DELIVERY) {
		return getStatusOnAcceptedFromLinkedTask(linkedTaskStatus);
	}
};

const getStatusOnAcceptedFromLinkedTask = (linkedTaskStatus: TaskStatus) => {
	if (linkedTaskStatus === TaskStatus.OnTheWay) {
		return OrderTrackingStatus.WayToPickup;
	}
	if ([TaskStatus.Done, TaskStatus.CheckedIn].includes(linkedTaskStatus)) {
		return OrderTrackingStatus.PickingUp;
	}
};

const getAcceptedStatus = (task: Task, hasRoute: boolean, linkedTaskStatus: TaskStatus) => {
	if (task.task_type_id === TaskTypeId.DELIVERY && linkedTaskStatus) {
		const trackingStatus = getStatusOnAcceptedFromLinkedTask(linkedTaskStatus);
		if (trackingStatus) return trackingStatus;
	}
	if (hasRoute) {
		return OrderTrackingStatus.OnPreviousDelivery;
	}
	if (isFirstWayPointDone(task)) {
		return OrderTrackingStatus.PickingUp;
	}
	return OrderTrackingStatus.DriverFound;
};

const getOnTheWayStatus = (task: Task) => {
	if (task.linked_task_id) {
		if (task.task_type_id === TaskTypeId.PICKUP) {
			return OrderTrackingStatus.WayToPickup;
		}
		if (task.task_type_id === TaskTypeId.DELIVERY) {
			return OrderTrackingStatus.WayToDropoff;
		}
	}
	return isFirstWaypointActive(task) ? OrderTrackingStatus.WayToPickup : OrderTrackingStatus.WayToDropoff;
};

const getCheckedInStatus = (task: Task) => {
	if (task.linked_task_id) {
		if (task.task_type_id === TaskTypeId.PICKUP) {
			return OrderTrackingStatus.PickingUp;
		}
		if (task.task_type_id === TaskTypeId.DELIVERY) {
			return OrderTrackingStatus.Arrived;
		}
	}
	return isFirstWaypointActive(task) ? OrderTrackingStatus.PickingUp : OrderTrackingStatus.Arrived;
};

const isFirstWayPointDone = (task: Task): boolean => {
	if (task.way_points?.length <= 1) return false;

	const firstWayPoint = getFirstWayPoint(task.way_points);
	return !!firstWayPoint?.done;
};

const isFirstWaypointActive = (task: Task): boolean => {
	if (!task.active_way_point_id) return false;
	if (task.way_points?.length <= 1) return false;

	const firstWayPoint = getFirstWayPoint(task.way_points);
	return firstWayPoint?.id === task.active_way_point_id;
};

const getFirstWayPoint = (wayPoints: WayPoint[] = []): WayPoint => {
	return wayPoints.reduce((prev, curr) => (prev.position < curr.position ? prev : curr));
};
